import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useCountries } from "use-react-countries";
import { MagnifyingGlassIcon, TrashIcon, UserPlusIcon, PencilIcon } from "@heroicons/react/24/solid";
import { Dialog, Transition } from '@headlessui/react';
import { Button, Input, Menu, MenuHandler, MenuList, MenuItem } from "@material-tailwind/react";
import { Input as AntInput, Modal, Tooltip } from "antd";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import RolesList from './components/RolesList';
import axiosInstance from "../../axios/axiosInstance";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";
import { apiFieldErrors, createTechnicianAccount } from "../../services/technicianRequestService";

export default function Users() {
    const { t } = useTranslation();
    const { countries } = useCountries();
    const [country, setCountry] = useState(177);
    const { name, flags, countryCallingCode } = countries[country];
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [userData, setUserData] = useState({
        name: "",
        email: "",
        login: "",
        password: "",
        confirmPassword: "",
        roles: [],
        phone: ""
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [techOpen, setTechOpen] = useState(false);
    const [techSaving, setTechSaving] = useState(false);
    const [techErrors, setTechErrors] = useState({});
    const [techForm, setTechForm] = useState({ name: '', login: '', phone: '', password: '' });

    const buildPhone = (raw) => {
        const digits = String(raw || '').replace(/\D/g, '');
        const code = String(countryCallingCode || '').replace(/\D/g, '');
        if (!digits) return '';
        if (code && digits.startsWith(code)) return digits;
        return `${code}${digits}`;
    };

    const roleName = (roles) => {
        if (Array.isArray(roles) && roles.length) return roles[0]?.label || roles[0]?.name || roles[0];
        if (typeof roles === 'string') return roles;
        return '';
    };

    const buildPayload = (data, { includePassword }) => {
        const payload = {
            name: data.name.trim(),
            login: data.login.trim(),
            phone: buildPhone(data.phone),
            roles: roleName(data.roles),
        };
        if (data.email?.trim()) payload.email = data.email.trim();
        if (includePassword) payload.password = data.password;
        return payload;
    };

    useEffect(() => {
        fetchUsers();
    }, []);


    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get("/admin/users");
            const list = response.data.data;
            setUsers(Array.isArray(list) ? list : []);
        } catch (error) {
            setUsers([]);
            toast.error(t('common.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    const createUser = async (form) => {
        if (!form.name.trim() || !form.login.trim() || !form.password) {
            toast.error(t('common.required'));
            return;
        }
        if (form.password !== form.confirmPassword) {
            toast.error(t('users.passwordMismatch'));
            return;
        }
        if (!roleName(form.roles)) {
            toast.error(t('roles.select'));
            return;
        }
        setIsLoading(true);
        try {
            const response = await axiosInstance.post('/admin/users', buildPayload(form, { includePassword: true }));
            setUsers((prev) => [...prev, response.data.data]);
            toast.success(t('users.created'));
            closeModal();
        } catch (error) {
            const message = error?.response?.data?.message || t('common.error');
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const updateUser = async (userId, form) => {
        if (!form.name.trim() || !form.login.trim()) {
            toast.error(t('common.required'));
            return;
        }
        if (form.password && form.password !== form.confirmPassword) {
            toast.error(t('users.passwordMismatch'));
            return;
        }
        setIsLoading(true);
        try {
            const response = await axiosInstance.put(
                `/admin/users/${userId}`,
                buildPayload(form, { includePassword: Boolean(form.password) })
            );
            setUsers((prev) => prev.map((user) => (user.id === userId ? response.data.data : user)));
            toast.success(t('users.updated'));
            closeModal();
        } catch (error) {
            const message = error?.response?.data?.message || t('common.error');
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteUser = async (userId) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/admin/users/${userId}`);
            setUsers((prev) => prev.filter((user) => user.id !== userId));
            toast.success(t('users.deleted'));
        } catch (error) {
            toast.error(error?.response?.data?.message || t('common.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setIsOpen(false);
        setEditUser(null);
        setUserData({
            name: "",
            email: "",
            login: "",
            password: "",
            confirmPassword: "",
            roles: [],
            phone: ""
        });
    };

    const openModal = () => {
        setIsOpen(true);
    };

    const closeTechModal = () => {
        setTechOpen(false);
        setTechErrors({});
        setTechForm({ name: '', login: '', phone: '', password: '' });
    };

    const createTechnician = async () => {
        if (!techForm.name.trim() || !techForm.login.trim() || !techForm.phone.trim() || !techForm.password) {
            toast.error(t('common.required'));
            return;
        }
        setTechSaving(true);
        setTechErrors({});
        try {
            await createTechnicianAccount({
                name: techForm.name.trim(),
                login: techForm.login.trim(),
                phone: techForm.phone.trim(),
                password: techForm.password,
            });
            toast.success(t('users.technicianCreated'));
            closeTechModal();
            fetchUsers();
        } catch (error) {
            const fields = apiFieldErrors(error);
            setTechErrors(fields);
            toast.error(error?.response?.data?.message || Object.values(fields)[0] || t('common.error'));
        } finally {
            setTechSaving(false);
        }
    };

    const openEditDialog = (user) => {
        setEditUser(user);
        const list = Array.isArray(user.roles) ? user.roles : user.roles ? [user.roles] : [];
        setUserData({
            name: user.name,
            email: user.email,
            login: user.login,
            password: "",
            confirmPassword: "",
            roles: list.map((role) => (typeof role === 'string' ? { label: role, value: role } : { label: role.name ?? role.label, value: role.id ?? role.value })),
            phone: user.phone
        });
        setIsOpen(true);
    };

    const renderRoles = (roles) => {
        const list = Array.isArray(roles) ? roles : roles ? [roles] : [];
        if (!list.length) return <span className="text-xs text-slate-400">{t('common.dash')}</span>;
        return (
        <div className="flex flex-wrap items-center gap-1">
            {list.map((role, index) => (
                <StatusBadge key={index} tone="brand">{typeof role === 'string' ? role : role?.name ?? role?.label}</StatusBadge>
            ))}
        </div>
        );
    };

    const filteredUsers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter((user) =>
            [user.name, user.login, user.phone, user.email, ...(Array.isArray(user.roles) ? user.roles : [user.roles])]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(q))
        );
    }, [users, searchQuery]);

    const columns = [
        {
            title: t('common.name'),
            dataIndex: "name",
            key: "name",
            render: (text, user) => (
                <div className="min-w-0">
                    <span className="block text-sm font-medium text-[#1D2C7B] truncate">{text}</span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">{user.email}</span>
                </div>
            ),
        },
        {
            title: t('common.login'),
            dataIndex: "login",
            key: "login",
            render: (login) => <span className="text-sm text-slate-700">{login}</span>,
        },
        {
            title: t('common.phone'),
            dataIndex: "phone",
            key: "phone",
            width: 160,
            render: (phone) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{phone}</span>
            ),
        },
        {
            title: t('users.role'),
            dataIndex: "roles",
            key: "roles",
            width: 170,
            render: renderRoles,
        },
        {
            title: t('common.actions'),
            key: "action",
            width: 88,
            fixed: 'right',
            render: (_, user) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title={t('users.edit')}>
                        <RowIconButton onClick={() => openEditDialog(user)}>
                            <PencilIcon className="h-[18px] w-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                    <Tooltip title={t('users.delete')}>
                        <RowIconButton onClick={() => deleteUser(user.id)}>
                            <TrashIcon className="h-[18px] w-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const renderMobileCard = (user) => (
        <div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B] truncate">{user.name}</span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">{user.login}</span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <RowIconButton onClick={() => openEditDialog(user)} aria-label={t('users.edit')}>
                        <PencilIcon className="h-[18px] w-[18px]" />
                    </RowIconButton>
                    <RowIconButton onClick={() => deleteUser(user.id)} aria-label={t('users.delete')}>
                        <TrashIcon className="h-[18px] w-[18px]" />
                    </RowIconButton>
                </div>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {renderRoles(user.roles)}
                <a href={`tel:+${user.phone}`} className="text-xs text-[#1D2C7B] tabular-nums">
                    {user.phone}
                </a>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('users.title')}
                    count={filteredUsers.length}
                    filters={
                        <AntInput
                            allowClear
                            size="small"
                            prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                            placeholder={t('common.search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="md:!w-[220px]"
                        />
                    }
                    actions={
                        <div className="flex items-center gap-1.5">
                            <ActionButton icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => setTechOpen(true)}>
                                {t('users.addTechnician')}
                            </ActionButton>
                            <ActionButton icon={<UserPlusIcon className="h-4 w-4" />} onClick={openModal}>
                                {t('users.add')}
                            </ActionButton>
                        </div>
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={filteredUsers}
                    loading={isLoading}
                    mobileCard={renderMobileCard}
                    scrollX={820}
                    pagination={{ pageSize: 20, showSizeChanger: false }}
                />
            </PageCard>

            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10 " onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-2.5 sm:p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-4xl transform rounded-xl border border-slate-200/90 bg-white p-4 sm:p-6 text-left align-middle transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-base font-semibold leading-6 text-slate-800"
                                    >
                                        {editUser ? t('users.editTitle') : t('users.create')}
                                    </Dialog.Title>
                                    <div className="mt-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            <Input label={t('common.name')} size="lg" value={userData.name}
                                                   onChange={(e) => setUserData({ ...userData, name: e.target.value })} />
                                            <Input label={t('common.login')} size="lg" value={userData.login}
                                                   onChange={(e) => setUserData({ ...userData, login: e.target.value })} />
                                            <Input label={t('common.email')} size="lg" value={userData.email}
                                                   onChange={(e) => setUserData({ ...userData, email: e.target.value })} />
                                        </div>
                                        <div className="grid mt-4 grid-cols-1 sm:grid-cols-2 gap-4">
                                            <Input label={t('common.password')} size="lg" type="password" value={userData.password}
                                                   onChange={(e) => setUserData({ ...userData, password: e.target.value })} />
                                            <Input label={t('doctors.passwordRepeat')} size="lg" type="password" value={userData.confirmPassword}
                                                   onChange={(e) => setUserData({ ...userData, confirmPassword: e.target.value })} />
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <RolesList value={userData.roles}
                                                       onChange={(value) => setUserData({ ...userData, roles: value })} />
                                            <div className="flex">
                                                <Menu placement="bottom-start ">
                                                    <MenuHandler>
                                                        <Button
                                                            ripple={false}
                                                            variant="text"
                                                            color="blue-gray"
                                                            className="flex h-11 shrink-0 items-center gap-2 rounded-md rounded-r-none border border-r-0 border-blue-gray-200 bg-blue-gray-500/10 pl-3"
                                                        >
                                                            <img
                                                                src={flags.svg}
                                                                alt={name}
                                                                className="h-5 w-5 rounded-full object-cover"
                                                            />
                                                            {countryCallingCode}
                                                        </Button>
                                                    </MenuHandler>
                                                    <MenuList className="max-h-[20rem] max-w-[18rem]">
                                                        {countries.map(({ name, flags, countryCallingCode }, index) => {
                                                            return (
                                                                <MenuItem
                                                                    key={name}
                                                                    value={name}
                                                                    className="flex items-center gap-2"
                                                                    onClick={() => setCountry(index)}
                                                                >
                                                                    <img
                                                                        src={flags.svg}
                                                                        alt={name}
                                                                        className="h-5 w-5 rounded object-cover"
                                                                    />
                                                                    {name} <span className="ml-auto">{countryCallingCode}</span>
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </MenuList>
                                                </Menu>
                                                <Input size="lg"
                                                       type="tel"
                                                       placeholder={t('users.phonePlaceholder')}
                                                       className="rounded-md rounded-l-none !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                       labelProps={{
                                                           className: "before:content-none after:content-none",
                                                       }}
                                                       containerProps={{
                                                           className: "min-w-0",
                                                       }}
                                                       value={userData.phone}
                                                       onChange={(e) => setUserData({ ...userData, phone: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                                        <Button onClick={closeModal} variant="text" className="normal-case font-normal">
                                            {t('common.cancel')}
                                        </Button>
                                        <Button
                                            onClick={editUser ? () => updateUser(editUser.id, userData) : () => createUser(userData)}
                                            className="normal-case font-normal"
                                            style={{ backgroundColor: '#1D2C7B' }}
                                        >
                                            {editUser ? t('common.update') : t('common.save')}
                                        </Button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Modal
                title={t('users.addTechnician')}
                open={techOpen}
                onCancel={closeTechModal}
                onOk={createTechnician}
                confirmLoading={techSaving}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
                destroyOnClose
            >
                <div className="space-y-3 pt-1">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.name')}</label>
                        <AntInput
                            value={techForm.name}
                            maxLength={255}
                            onChange={(e) => setTechForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        {techErrors.name && <p className="mt-1 mb-0 text-xs text-red-500">{techErrors.name}</p>}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.login')}</label>
                        <AntInput
                            value={techForm.login}
                            maxLength={30}
                            onChange={(e) => setTechForm((prev) => ({ ...prev, login: e.target.value }))}
                        />
                        {techErrors.login && <p className="mt-1 mb-0 text-xs text-red-500">{techErrors.login}</p>}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.phone')}</label>
                        <AntInput
                            value={techForm.phone}
                            maxLength={13}
                            onChange={(e) => setTechForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 13) }))}
                            placeholder="998900000007"
                        />
                        {techErrors.phone && <p className="mt-1 mb-0 text-xs text-red-500">{techErrors.phone}</p>}
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.password')}</label>
                        <AntInput.Password
                            value={techForm.password}
                            onChange={(e) => setTechForm((prev) => ({ ...prev, password: e.target.value }))}
                        />
                        {techErrors.password && <p className="mt-1 mb-0 text-xs text-red-500">{techErrors.password}</p>}
                    </div>
                </div>
            </Modal>
        </PageShell>
    );
}
